<template>
  <div class="d-flex flex-column parent-container">
    <div class="nav-bar-layout-container">
      <TaazeNavbar />
    </div>
    <div class="flex-grow-1 layout-container chat-container">
      <div class="d-flex flex-column">
        <div class="chat-header chat-content align-self-center">Talk to the Books <div class="chat-sub-header">{{
          VERSION }} 版
          </div>
        </div>
        <div v-for="(chat, index) in chatHistory" :key="index" :chat-index="index">
          <ChatMessage :chat="chat" :is-prompting-enabled="isPromptingEnabled()"
            :is-share-enabled="isSharingEnabled(index, chat)"
            @on-prompt-suggestion-selected="onPromptSuggestionSelected" @on-share-click="onChatShare" />
        </div>

      </div>
    </div>
    <div v-if="product != null" class="product-card-chat-container layout-container">
      <ChatInput v-model="chatInputValue" :readonly="false" :is-chat-enabled="true" :is-focused="isChatFocused"
        :border-color="chatBorderColor" :icon-color="chatIconColor" :placeholder-color="chatPlaceholderColor"
        @on-message-send="onMessageSend" @on-focused="onChatFocused" @on-out-of-focus="onChatOutOfFocus" />

      <transition name="slide-fade">
        <ProductCard v-if="!isUserTyping" :product="product" />
      </transition>
    </div>
  </div>

  <InfoModalComponent ref="infoModalComponent" :header="INSUFFICIENT_FUNDS_MODAL_TITLE" :body="INSUFFICIENT_FUNDS_MODAL_BODY" button-text="如何賺T點"
    @on-button-click="onModalButtonClick" />

  <AlertModalComponent ref="alertModalComponent" :header="TIMEOUT_ERROR_MODAL_TITLE" button-text="關閉"
    @on-button-click="null" />

</template>

<script setup lang="ts">
import { useRoute } from 'vue-router'
import { ref, nextTick, computed, onMounted, watch } from 'vue';
import { TalkToTheBookAIService } from '@/services/TalkToTheBookAIService';
import { AuthorizationService } from '@/services/AuthorizationService';
import { SharedResponseService } from '@/services/SharedResponseService';
import { SocialMediaShareService } from '@/services/SocialMediaShareService';
import { AiPointService } from '@/services/AiPointService';
import { TaazeProductUrlCreator } from '@/utils/taazeProductUrlCreator';
import { scrollToBottom, scrollTo } from '@/utils/scrollUtils';
import { v4 as uuidv4 } from 'uuid';

import { TAAZE_USER_PAGE_URL, TAAZE_T_POINT_HISTORY_PAGE_URL, OCOPY_WELCOME_MESSAGE, SHARE_RESPONSE_MESSAGE } from '@/assets/strings';
import { TAAZE_MAIN_COLOR } from '@/assets/colors';

import ProductCard from '@/components/ProductCard.vue';
import ChatInput from '@/components/ui/ChatInput.vue';
import TaazeNavbar from '@/components/layout/TaazeNavbar.vue';
import ChatMessage from '@/components/ChatMessage.vue'
import InfoModalComponent from '@/components/ui/InfoModalComponent.vue';
import AlertModalComponent from '@/components/ui/AlertModalComponent.vue';
import Chat from '@/interfaces/Chat';
import Product from '@/models/Product';
import ProductRepository from '@/repositories/ProductRepository';
import TalkToTheBookSource from '@/interfaces/TalkToTheBookSource';
import InsufficientPointsError from '@/errors/InsufficientPointsError';

const DISABLED_COLOR = '#E0E0E0';
const BASE_PROMPT = '請幫我總結這本書';
const VERSION = process.env.VUE_APP_VERSION;

const productId: string | undefined = useRoute().query.product_id?.toString();
const userId: string | undefined = useRoute().query.user_id?.toString();
const deafultPrompt: string | undefined = (useRoute().query.prompt?.toString() ?? '').trim();
const firstQuestionIndex = Number(useRoute().query.q ?? 0);

const talkToTheBookAIService = new TalkToTheBookAIService(process.env.VUE_APP_TALK_TO_THE_BOOK_API_URL!);
const aiPointService = new AiPointService(process.env.VUE_APP_TALK_TO_THE_BOOK_API_URL!);
const authorizationService = new AuthorizationService(process.env.VUE_APP_TALK_TO_THE_BOOK_API_URL!);
const sharedResponseService = new SharedResponseService(process.env.VUE_APP_TALK_TO_THE_BOOK_API_URL!);
const socialMediaShareService = new SocialMediaShareService();
const taazeProductUrlCreator = new TaazeProductUrlCreator();
const productRepository = new ProductRepository(process.env.VUE_APP_TAAZE_API_URL!);

const chatHistory = ref<Chat[]>([]);
const userPoints = ref(0);
const infoModalComponent = ref<InstanceType<typeof InfoModalComponent> | null>(null);
const alertModalComponent = ref<InstanceType<typeof AlertModalComponent> | null>(null);
const chatInputValue = ref('');
const onPrompting = ref(false);
const isChatFocused = ref(false);
const isUserTyping = computed(() => chatInputValue.value !== '' || isChatFocused.value);
const chatBorderColor = computed(() => (chatInputValue.value !== '' && !isChatFocused.value) || onPrompting.value ? DISABLED_COLOR : TAAZE_MAIN_COLOR);
const chatIconColor = computed(() => onPrompting.value ? DISABLED_COLOR : TAAZE_MAIN_COLOR);
const chatPlaceholderColor = computed(() => onPrompting.value ? DISABLED_COLOR : TAAZE_MAIN_COLOR);

const welcomeMessage = computed(() => `${OCOPY_WELCOME_MESSAGE}\\
  您目前擁有的T點：共[{${TAAZE_MAIN_COLOR}}(${userPoints.value})](${TAAZE_T_POINT_HISTORY_PAGE_URL}#nounderline)點

  每次體驗AI閱讀服務只需 20 T點！

  如何獲得T點？[立即查看詳情](${TAAZE_USER_PAGE_URL})`);

let welcomeMessageChatId: string | null = null;

let sessionId = '';
let product: Product | null = null;
let aiPointReservationId = '';
let predefinedQuestions: { [key: string]: string } | null = null;
let promptCounter = 0;
let lastUserChatHeight = 0;
let streamBuffer = '';
let contentTimer: ReturnType<typeof setTimeout> | number;

const TIMEOUT_ERROR_MODAL_TITLE = '系統忙碌中，請稍後再使用，謝謝。';
const INSUFFICIENT_FUNDS_MODAL_TITLE = '現有T點不足！ 每次提問需20T點。';
const INSUFFICIENT_FUNDS_MODAL_BODY = computed(() => `您目前擁有的T點：共<span style="color: ${TAAZE_MAIN_COLOR}";>${userPoints.value}</span>點`);

onMounted(async () => {
  await setup();
});

watch(userPoints, () => {
  if (welcomeMessageChatId !== null) {
    const welcomeChat = chatHistory.value.find(chat => chat.id === welcomeMessageChatId);
    if (welcomeChat) {
      welcomeChat.content = welcomeMessage.value;
    }
  }
});

async function setup() {
  if (productId === undefined || userId === undefined) {
    return;
  }

  sessionId = await authorizationService.startSession(userId);
  product = await productRepository.getProductById(productId);
  predefinedQuestions = await talkToTheBookAIService.getPredefinedQuestions();
  userPoints.value = await aiPointService.getUserPoints(userId);

  const welcomeChat = addNewChat('bot', welcomeMessage.value, [], false, false);
  welcomeMessageChatId = welcomeChat.id;

  const firstQuestion = deafultPrompt ? deafultPrompt.trim() : predefinedQuestions[firstQuestionIndex] ?? BASE_PROMPT;

  await prompt(firstQuestion);

  window.addEventListener("beforeunload", () => {
    authorizationService.sessionEnd(sessionId, true);
  });
}

function onContentStreamed(content: string | null, promptSuggestions: string | null, sources: TalkToTheBookSource[]) {
  if (chatHistory.value.length === 2) {
    content = '';
  }

  const sliceAmount = (promptSuggestions !== null) ? 10 : 3;
  const timeout = 7;

  const lastBotChat = chatHistory.value.findLast(chat => chat.type === 'bot');

  if (lastBotChat) {
    lastBotChat.isLoading = false;

    // Append content to a buffer and start the timer if not already started
    streamBuffer += (content === null) ? '' : content;

    if (contentTimer) {
      clearTimeout(contentTimer);
    }

    // Consuming reservations here in the case that the user is in another tab. Which case the user would have to
    //  finish showing the response, which would delay the point consumption.
    if (promptSuggestions !== null && aiPointReservationId === '') {
      consumePointReservation();
    }

    // Function to output the content smoothly with timing control
    const outputContent = () => {
      if (streamBuffer.length > 0) {
        const charsToAdd = streamBuffer.slice(0, sliceAmount);
        lastBotChat.content += charsToAdd;
        streamBuffer = streamBuffer.slice(sliceAmount);
        contentTimer = setTimeout(outputContent, timeout);
      } else if (promptSuggestions !== null) {
        onStreamComplete(promptSuggestions, sources);
        return;
      }

      scrollToLastChat();
    };

    // Start the output process with a delay before adding the first character
    contentTimer = setTimeout(outputContent, timeout);
  }
}

function onStreamComplete(promptSuggestions: string, sources: TalkToTheBookSource[]) {
  if (userId === undefined) {
    return;
  }

  promptCounter++;

  sources.sort((a, b) => {
    return b.prod_title_main.length - a.prod_title_main.length;
  });

  if (chatHistory.value.length === 2) {
    chatHistory.value.pop();
  }

  const lastBotMessage = chatHistory.value[chatHistory.value.length - 1];
  lastBotMessage.suggestions = promptSuggestions.split('\n').filter(suggestion => suggestion.trim() !== '');
  lastBotMessage.isGenerating = false;

  onPrompting.value = false;

  if (aiPointReservationId !== '') {
    consumePointReservation();
  }

  scrollToLastChat();
}

async function prompt(prompt: string) {
  if (product === null || userId === undefined) {
    return;
  }

  try {
    onPrompting.value = true;

    addNewChat('user', prompt.replace(/^\d+\.\s/, ''), [], true);

    addNewChat();
    scrollToChatBottom(true);

    await aiPointService.reservePoints(userId).then((reservationId: string) => {
      aiPointReservationId = reservationId;
    });

    await talkToTheBookAIService.prompt(product.id, prompt, userId, sessionId, onContentStreamed);

  } catch (error: any) {
    if (error instanceof InsufficientPointsError) {
      infoModalComponent.value?.show();
    }

    if (error instanceof Error && error.name === 'AbortError') {
      alertModalComponent.value?.show();
    }

    removeLastChat();
    cancelPointReservation();
    onPrompting.value = false;
  }
}

async function consumePointReservation() {
  if (userId === undefined) {
    return;
  }

  if (aiPointReservationId !== '') {
    await aiPointService.consumePoints(userId, aiPointReservationId).then(() => {
      aiPointReservationId = '';
    }).catch((error) => {
      console.error('Error during point consumption');
    });
  }

  aiPointService.getUserPoints(userId).then((points: number) => {
    userPoints.value = points;
  }).catch((error) => {
    console.error('Error fetching user points');
  });
}

function cancelPointReservation() {
  if (userId === undefined) {
    return;
  }

  if (aiPointReservationId !== '') {
    aiPointService.cancelPoints(userId, aiPointReservationId).then(() => {
      aiPointReservationId = '';
    }).catch((error) => {
      console.error('Error during point cancellation');
    });
  }
}

function onMessageSend() {
  if (!onPrompting.value && chatInputValue.value !== '') {
    prompt(chatInputValue.value);
    resetChatInputState();
  }
}

function onPromptSuggestionSelected(suggestion: string) {
  if (!onPrompting.value) {
    prompt(suggestion);
  }
}

async function onChatShare(platform: 'facebook' | 'line', chat: Chat) {
  if (productId === undefined || userId === undefined) {
    return;
  }

  let previousChat: Chat | undefined = undefined;
  for (let i = 1; i < chatHistory.value.length; i++) {
    if (chatHistory.value[i].id === chat.id) {
      previousChat = chatHistory.value[i - 1];
      break;
    }
  }

  if (previousChat === undefined) {
    return;
  }

  const shareUrl = await sharedResponseService.createShareLink(productId, userId, previousChat.content, chat.content);
  const productUrl = taazeProductUrlCreator.createProductUrl(productId);

  const shareMessage = `${product?.name}，${SHARE_RESPONSE_MESSAGE}\n\n${shareUrl}`;

  if (platform === 'line') {
    const share_url = socialMediaShareService.getLineShareUrl(productUrl, shareMessage);
    window.open(share_url, '_blank');
  }

  if (platform === 'facebook') {
    const share_url = socialMediaShareService.getFacebookShareUrl(shareUrl, shareMessage);
    window.open(share_url, '_blank');
  }
}

function isPromptingEnabled() {
  return !onPrompting.value;
}

function isSharingEnabled(chatIndex: number, chat: Chat) {
  return (chatIndex > 0 && chat.type !== 'user' && chat.isLoading === false && chat.isGenerating === false);
}

function onChatFocused() {
  isChatFocused.value = true;

  const scrollableDiv = document.getElementsByClassName('chat-container')[0] as HTMLElement;
  const isAtBottom = scrollableDiv.scrollHeight - scrollableDiv.scrollTop <= scrollableDiv.clientHeight + 10;

  if (!isAtBottom && chatInputValue.value === '') {
    scrollTo(scrollableDiv, scrollableDiv.scrollTop - 130, 'instant', 0);
  }
}

function onChatOutOfFocus() {
  isChatFocused.value = false;

  const scrollableDiv = document.getElementsByClassName('chat-container')[0];
  const isAtBottom = scrollableDiv.scrollHeight - scrollableDiv.scrollTop <= scrollableDiv.clientHeight + 10;

  if (!isAtBottom && !isUserTyping.value) {
    nextTick(() => {
      scrollableDiv.scrollTop += 130;
    });

    return;
  }
}

function onModalButtonClick() {
  window.open(TAAZE_USER_PAGE_URL, '_blank');
}

function calculateLastUserChatHeight() {
  if (chatHistory.value.length === 0) {
    return 0;
  }

  nextTick(() => {
    lastUserChatHeight = 0;

    const lastUserChatIndex = chatHistory.value.findLastIndex(chat => chat.type === 'user');

    for (let index = 0; index < chatHistory.value.length; index++) {
      if (index > lastUserChatIndex) {
        break;
      }

      const chatElement = document.querySelector(`[chat-index="${index}"]`) as HTMLElement;
      lastUserChatHeight += chatElement.clientHeight;
    }
  });
}

function addNewChat(userType: 'user' | 'bot' = 'bot', content = '', suggestions = [], isLoading = true, isGenerating = true): Chat {
  const newChat: Chat = {
    id: uuidv4(),
    type: userType,
    content: content,
    suggestions: suggestions,
    isLoading: isLoading,
    isGenerating: isGenerating
  };
  
  chatHistory.value.push(newChat);

  if (userType === 'user') {
    calculateLastUserChatHeight();
  }
  
  return newChat;
}

function removeLastChat() {
  chatHistory.value.pop();
}

function scrollToChatBottom(forced = false) {
  const chatBox = document.getElementsByClassName('chat-container')[0] as HTMLElement;
  scrollToBottom(chatBox, forced);
}

function scrollToLastChat() {
  const chatBox = document.getElementsByClassName('chat-container')[0] as HTMLElement;
  const isAtBottom = chatBox.scrollHeight - chatBox.scrollTop <= chatBox.clientHeight + 50;

  const lastUserChatIndex = chatHistory.value.findLastIndex(chat => chat.type === 'user');
  const chatElement = document.querySelector(`[chat-index="${lastUserChatIndex}"]`) as HTMLElement;
  const chatElementClientRect = chatElement.getBoundingClientRect();

  // Only scroll if the chat box is at the bottom and the last user chat is visible
  if (isAtBottom && chatElementClientRect.y > 0) {
    scrollTo(chatBox, lastUserChatHeight, 'smooth', 50);
  }
}

function resetChatInputState() {
  chatInputValue.value = '';
  isChatFocused.value = false;
}
</script>

<style scoped>
.parent-container {
  padding: 0;
  margin: 0;
  height: 100dvh;
  background-color: #F5EEE6;
}

.layout-container {
  padding-left: 16px;
  padding-right: 16px;
}

.chat-container {
  padding-top: 16px;
  padding-bottom: 16px;
  overflow: auto;
}

.chat-container::-webkit-scrollbar {
  width: 12px;
  height: 12px;
}

.chat-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.chat-container::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 10px;
}

.chat-container::-webkit-scrollbar-thumb:hover {
  background: #555;
}

.chat-header {
  font-family: 'Noto Sans TC', sans-serif;
  font-size: 24px;
  font-weight: 600;
  color: #68444A;
}

.chat-content {
  margin-bottom: 16px;
  gap: 16px;
}

.chat-sub-header {
  display: inline;
  font-family: 'Noto Sans TC', sans-serif;
  font-size: 14px;
  font-weight: 400;
}

.product-card-chat-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
  background-color: white;
  padding-top: 16px;
  padding-bottom: 16px;
}

@media (min-width: 768px) {
  .layout-container {
    padding-left: 78px;
    padding-right: 78px;
  }
}

@media (min-width: 1200px) {
  .layout-container {
    padding-left: 100px;
    padding-right: 100px;
  }

  .nav-bar-layout-container {
    padding-left: 100px;
    padding-right: 100px;
  }
}

.slide-fade-enter-active {
  transition: all 0.6s ease-out;
}

.slide-fade-enter-from {
  opacity: 0;
  transform: translateY(-10px);
}
</style>